import { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import type { ILocationSelectItem } from "../../../customTypings/customer/Customer";
import { customerLocationAssociationsService } from "../../../services/customers/customerLocationAssociations.service";
import { ReactSelectBootstrapStyle } from "../../../styles/ReactSelectBootstrapStyle";
import { getUserFacingErrorMessage, showToastError, showToastSuccess } from "../../../utils";

export interface AddCustomerLocationModalProps {
    show: boolean;
    siteId: string;
    customerId: string;
    onClose: () => void;
    onAssociated: () => void;
}

const AddCustomerLocationModal = ({
    show,
    siteId,
    customerId,
    onClose,
    onAssociated,
}: AddCustomerLocationModalProps) => {
    const navigate = useNavigate();
    const [locations, setLocations] = useState<ILocationSelectItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [associating, setAssociating] = useState(false);
    const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
    const [isMainLocation, setIsMainLocation] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    useEffect(() => {
        if (!show) {
            setSelectedLocationId(null);
            setIsMainLocation(false);
            setLoadError(null);
            return;
        }
        setLoading(true);
        customerLocationAssociationsService
            .listAvailableLocations(siteId, customerId)
            .then((rows) => {
                setLocations(rows);
                setLoadError(null);
            })
            .catch((e: unknown) => {
                setLoadError(getUserFacingErrorMessage(e, "Could not load available locations."));
            })
            .finally(() => setLoading(false));
    }, [show, siteId, customerId]);

    const options = useMemo(
        () =>
            locations.map((l) => ({
                value: l.id,
                label: l.postcode ? `${l.name} (${l.postcode})` : l.name,
            })),
        [locations]
    );

    const selectedOption = useMemo(
        () => options.find((o) => o.value === selectedLocationId) ?? null,
        [options, selectedLocationId]
    );

    const handleAssociate = () => {
        if (!selectedLocationId) return;
        setAssociating(true);
        customerLocationAssociationsService
            .associateLocation(siteId, customerId, {
                locationId: selectedLocationId,
                isMainLocation,
            })
            .then(() => {
                showToastSuccess("Location linked.");
                onAssociated();
            })
            .catch((e: unknown) => {
                showToastError(getUserFacingErrorMessage(e, "Could not link location."));
            })
            .finally(() => setAssociating(false));
    };

    return (
        <Modal show={show} onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Add Location</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loadError && <div className="text-danger mb-3">{loadError}</div>}
                <div className="d-flex justify-content-end" style={{ marginBottom: "-10px" }}>
                    <Button
                        variant="outline-primary"
                        onClick={() => {
                            onClose();
                            navigate(`/customers/${customerId}/new-location`);
                        }}
                    >
                        New Location
                    </Button>
                </div>
                <div className="mb-3">
                    <label className="form-label" htmlFor="add-customer-location-select">
                        Location
                    </label>
                    <Select
                        inputId="add-customer-location-select"
                        options={options}
                        value={selectedOption}
                        onChange={(option) => setSelectedLocationId(option?.value ?? null)}
                        isLoading={loading}
                        isClearable
                        placeholder={loading ? "Loading…" : "Select a location…"}
                        styles={ReactSelectBootstrapStyle}
                        classNamePrefix="react-select"
                    />
                </div>
                <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between">
                    <Form.Check
                        type="checkbox"
                        id="add-customer-location-main"
                        label="Set as primary address"
                        checked={isMainLocation}
                        onChange={(e) => setIsMainLocation(e.target.checked)}
                        disabled={!selectedLocationId || associating || loading}
                    />
                    <Button
                        variant="primary"
                        onClick={handleAssociate}
                        disabled={!selectedLocationId || associating || loading}
                    >
                        {associating ? "Linking…" : "Link location"}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export { AddCustomerLocationModal };
